import { useFormik } from 'formik';
import { ReactComponent as GuaranteeIcon } from '@packages/shared/assets/guarantee-gradient.svg';
import { PhoneNumberScreenProps, PhoneNumberWithRedirectScreenProps } from '@packages/shared/core/quiz/entities';
import Image from '@packages/shared/core/common/components/common/Image';
import Header from '@packages/shared/core/common/components/layout/Header';
import Button, { TextButton } from '@packages/shared/core/common/components/common/Button';
import Checkbox from '@packages/shared/core/common/components/common/Checkbox';
import PhoneField from '@packages/shared/core/common/components/common/PhoneField';
import { isValidPhoneNumber } from '@packages/shared/core/common/utils/phoneNumber';
import { IsFeatureOff, IsFeatureOn } from '@packages/shared/core/feature-flags/ui';
import { phoneNumberOptInFeature } from '@packages/shared/core/feature-flags/config';
import RenderOnlyOnClient from '@packages/shared/core/common/components/common/RenderOnlyOnClient';
import Link from '@packages/shared/core/common/components/common/Link';
import { mainAppRoutes } from '@packages/shared/core/common/constants/routes';

import { DataTestId } from '@packages/shared/core/common/constants/dataTestId';
import styles from './PhoneNumberScreen.module.scss';

const PhoneNumberScreen = ({
  onSubmit,
  skip,
  userCountry,
  content,
}: PhoneNumberScreenProps | PhoneNumberWithRedirectScreenProps) => {
  const { handleSubmit, getFieldProps, values, setFieldValue, touched, errors, isValid, isSubmitting } = useFormik({
    initialValues: {
      phoneNumber: '',
      smsConsent: false,
    },
    validateOnMount: true,
    onSubmit: async ({ phoneNumber }) => {
      await onSubmit(phoneNumber);
    },
    validate: ({ phoneNumber, smsConsent }) => {
      if (!isValidPhoneNumber(phoneNumber)) {
        return {
          phoneNumber: 'The phone number you entered is not valid',
        };
      }

      if (!smsConsent) {
        return { smsConsent: 'Your agreement is required' };
      }

      return {};
    },
  });

  const onInputChange = (phoneNumber: string) => {
    setFieldValue('phoneNumber', phoneNumber);
  };

  const error = (touched.phoneNumber ? errors.phoneNumber : '') || (touched.smsConsent ? errors.smsConsent : '');
  const isSubmitDisabled = !isValid || isSubmitting;

  return (
    <div className={styles.wrapper}>
      <Header className={styles.header} menuClassName={styles.menu} />

      <div className={styles.content}>
        <div className={styles.overview}>
          <div className={styles.imageWrapper}>
            <Image
              className={styles.image}
              width={100}
              height={100}
              src={content.titleImage.src}
              alt={content.titleImage.alt}
            />
          </div>

          <h2 className={styles.title}>{content.title}</h2>

          <div className={styles.descriptionListWrapper}>
            <ul className={styles.descriptionList}>
              {content.description.map((descriptionItem) => (
                <li className={styles.descriptionListItem} key={descriptionItem}>
                  <p className={styles.description}>{descriptionItem}</p>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.guarantee}>
            <GuaranteeIcon className={styles.guaranteeIcon} />

            {content.guaranteeText}
          </div>
        </div>

        <div className={styles.formWrapper}>
          <form onSubmit={handleSubmit} className={styles.form}>
            <PhoneField
              {...getFieldProps('phoneNumber')}
              wrapperProps={{ className: styles.phoneWrapper }}
              onChange={onInputChange}
              defaultCountry={userCountry}
              error={error}
            />

            <div className={styles.consentWrapper}>
              <Checkbox
                className={styles.consent}
                labelClassName={styles.consentLabel}
                {...getFieldProps('smsConsent')}
                checked={values.smsConsent}
              >
                <RenderOnlyOnClient>
                  <IsFeatureOn name={phoneNumberOptInFeature}>
                    {`By checking this box, you agree to receive SMS messages from Nebula regarding account updates and
                    exclusive promotions. Message and data rates may apply. View our Terms & Privacy Policy `}

                    <Link href={mainAppRoutes.privacy} target="_blank" className={styles.link}>
                      here
                    </Link>

                    {`. Reply STOP at any time to unsubscribe.`}
                  </IsFeatureOn>

                  <IsFeatureOff name={phoneNumberOptInFeature}>{content.checkboxApproveText}</IsFeatureOff>
                </RenderOnlyOnClient>
              </Checkbox>
            </div>

            <Button
              loading={isSubmitting}
              disabled={isSubmitDisabled}
              type="submit"
              wrapperClassName={styles.submitWrapper}
              className={styles.submit}
            >
              {content.continueButtonText}
            </Button>

            <TextButton
              data-testid={DataTestId.REJECT_PHONE_UPDATES_BUTTON}
              onClick={skip}
              wrapperClassName={styles.skipWrapper}
              className={styles.skip}
            >
              {content.skipButtonText}
            </TextButton>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PhoneNumberScreen;
